/**
 * ComboboxInfinite — virtualized combobox for searching across entity types.
 *
 * Use ONLY when the dropdown must search multiple entity types simultaneously
 * (e.g. trade names + consumables merged into one list). For single-entity
 * lookups, prefer `<InfiniteCombobox>` which uses `useZeroVirtualizer` for
 * proper bidirectional pagination and server-side search.
 *
 * Example (cross-type search):
 * ```
 * const {tradeNames} = useGetTradeNames({filters: {search}})
 * const {consumables} = useGetConsumables({filters: {search}})
 *
 * <ComboboxInfinite
 *   items={[...tradeNames, ...consumables]}
 *   transformItems={items => items.map(i => ({label: i.name, value: i.id}))}
 *   onSearchChange={setSearch}
 * />
 * ```
 */
import {
  Box,
  Combobox as ChakraCombobox,
  ComboboxRootProps,
  InputGroup,
  Portal,
  Span,
  Text,
  useListCollection,
} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useCallback, useMemo} from 'react'
import {LuSearch} from 'react-icons/lu'
import {Virtuoso} from 'react-virtuoso'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {SelectItem} from './select.js'
import {useSelectField} from './use-select-field.js'

interface Props<RowData> extends Omit<ComboboxRootProps, 'collection'> {
  items: RowData[]
  label: string
  hideLabel?: boolean
  placeholder?: string
  controlProps?: ChakraCombobox.ControlProps
  contentProps?: ChakraCombobox.ContentProps
  portalled?: boolean
  disabled?: boolean
  onSearchChange?: (value: string) => void
  transformItems: (items: RowData[]) => SelectItem[]
}

const ROW_HEIGHT = 32

export function ComboboxInfinite<RowData>({items, ...props}: Props<RowData>) {
  const {t} = useTranslate()
  const {field, handleChange, value} = useSelectField()

  const rowDataItems = useMemo(() => {
    return props.transformItems(items)
  }, [items])

  const {collection} = useListCollection({
    initialItems: rowDataItems,
  })

  const handleValueChange = useCallback(
    (e: ChakraCombobox.ValueChangeDetails) => {
      if (e.value.length === 0) {
        handleChange([])
        return
      }

      if (e.value[0] === 'EMPTY_STATE') return

      handleChange(e.value)
    },
    [field, handleChange]
  )

  return (
    <FieldRoot required={props.required} disabled={props.disabled}>
      <ChakraCombobox.Root
        width="100%"
        collection={collection}
        name={field.name}
        value={value}
        disabled={props.disabled}
        onInputValueChange={e => props.onSearchChange?.(e.inputValue)}
        onValueChange={handleValueChange}
        openOnClick
        positioning={{sameWidth: false, placement: 'bottom-start'}}
        onBlur={field.handleBlur}
        {...props}
      >
        <ChakraCombobox.Label srOnly={props.hideLabel}>
          {props.label} {props.required && <FieldRequiredIndicator />}
        </ChakraCombobox.Label>

        <ChakraCombobox.Control backgroundColor="bg" {...props.controlProps}>
          <InputGroup startElement={<LuSearch />}>
            <ChakraCombobox.Input placeholder={props.placeholder} />
          </InputGroup>
          <ChakraCombobox.IndicatorGroup>
            <ChakraCombobox.ClearTrigger />
            <ChakraCombobox.Trigger />
          </ChakraCombobox.IndicatorGroup>
        </ChakraCombobox.Control>

        <Portal disabled={!props.portalled}>
          <ChakraCombobox.Positioner width="100%">
            <ChakraCombobox.Content minWidth="20rem" {...props.contentProps}>
              {rowDataItems.length > 0 ? (
                <Virtuoso
                  style={{height: `${Math.min(rowDataItems.length * ROW_HEIGHT, 320)}px`}}
                  data={rowDataItems}
                  increaseViewportBy={15}
                  itemContent={(_index, item) => {
                    return (
                      <ChakraCombobox.Item key={item.value} item={item}>
                        <Box display="flex" flexDirection="column">
                          <Span whiteSpace="pre-line">{item.label}</Span>
                          {item.description !== undefined && (
                            <Span fontSize="xs" color="fg.muted">
                              {item.description}
                            </Span>
                          )}
                        </Box>
                        <ChakraCombobox.ItemIndicator />
                      </ChakraCombobox.Item>
                    )
                  }}
                />
              ) : (
                <Text p="2">{t('no_options_available')}</Text>
              )}
            </ChakraCombobox.Content>
          </ChakraCombobox.Positioner>
        </Portal>
      </ChakraCombobox.Root>
      <ErrorMessage />
    </FieldRoot>
  )
}
